/** @jsx jsx */
import { Children } from 'react';
import { jsx } from '@emotion/react';

export interface ThemedImgProps
  extends Pick<
      React.ImgHTMLAttributes<HTMLImageElement>,
      'className' | 'id' | 'height' | 'width'
    >,
    Pick<Required<React.ImgHTMLAttributes<HTMLImageElement>>, 'alt'> {
  src: { light: string; dark: string };
}

/**
 * Creates a themed image, which alternates it's display source
 * based on the current theme.
 *
 * Accepts all `<img>` attributes as props.
 *
 * @example
 * <ThemedImg
 *  src={{
 *    light: 'light-image.png',
 *    dark: 'dark-image.png',
 *   }}
 *  width={400}
 *  height={300}
 *  alt="Nice, a themed image"
 * />
 */
const ThemedImg = ({
  src,
  alt,
  className,
  id,
  height,
  width,
}: ThemedImgProps) => {
  return (
    <img
      css={[
        {
          maxWidth: '100%',
          height: 'auto',
          /**
           * Fallback to light image if no theme is set.
           *
           * This is applied here in CSS rather than the
           * image `src` attribute, because otherwise the light
           * assets will begin downloading in dark mode
           * before themed CSS is applied.
           */
          content: `url(${src['light']})`,
        },
        Object.entries(src).map(([theme, source]) => ({
          [`html[data-color-mode=${theme}] &`]: {
            content: `url(${source})`,
          },
        })),
      ]}
      className={className}
      id={id}
      height={height}
      width={width}
      alt={alt}
    />
  );
};

export interface ThemedImgMdxProps extends Omit<ThemedImgProps, 'src'> {
  children: React.ReactElement<
    HTMLImageElement & { originalType: string; transformedType: string }
  >[];
}

/**
 * Creates a themed image in MDX.
 *
 * Due to limitations with the 'gatsby-remark-copy-linked-files' plugin
 * (which copies local files linked to/from Markdown), image sources
 * cannot be extracted from custom components. This workaround requires use of
 * <img> tags to catch the transformed `src` and reroute them to the
 * {@link ThemedImg `ThemedImg` component}`
 *
 * @example
 * <ThemedImgMdx width={400} height={300} alt="Nice, a themed image... but in MDX">
 *   <img src="images/light-image.png" data-theme="light" />
 *   <img src="images/dark-image.png" data-theme="dark" />
 * </ThemedImgMdx>
 */
const ThemedImgMdx = ({
  children,
  alt,
  className,
  id,
  height,
  width,
}: ThemedImgMdxProps) => {
  let src = {};
  Children.forEach(children, ({ props }) => {
    if (props.originalType !== 'img') {
      return;
    }

    // @ts-expect-error
    src[props['data-theme']] = props.src;
  });

  return (
    <ThemedImg
      src={src as ThemedImgProps['src']}
      className={className}
      id={id}
      height={height}
      width={width}
      alt={alt}
    />
  );
};

export default ThemedImg;
export { ThemedImgMdx };
